import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Send, Building2, Instagram, Phone } from "lucide-react";
import { INSTAGRAM_URL } from "@/components/Footer";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/393522500222?text=Buongiorno%2C%20vorrei%20informazioni%20per%20un%20trattamento%20a%20domicilio.";

const SERVICES = [
  "Fisioterapia per Anziani",
  "Riabilitazione Post-Operatoria",
  "Ritorno alle Attività Quotidiane e Sportive",
  "Trattamento dei Dolori Cronici",
  "Recupero Post-Infortunio",
  "Trattamento in Studio (Fisioterapia Italia Torino)",
  "Valutazione generale",
];

const INFO = [
  { icon: MapPin, label: "Zone servite", value: "Torino · Moncalieri · Trofarello · Nichelino · Cambiano · Revigliasco · Pecetto" },
  { icon: Building2, label: "In studio", value: "Fisioterapia Italia Torino — Via Marenco 28/B, Torino", href: "https://www.google.com/maps/search/?api=1&query=Via+Marenco+28%2FB+Torino" },
  { icon: Mail, label: "Email", value: "mattec.fisio@gmail.com", href: "mailto:mattec.fisio@gmail.com" },
  { icon: Instagram, label: "Instagram", value: "@matteo_caravetta_fisio", href: INSTAGRAM_URL },
];

export default function Contact() {
  const [form, setForm] = useState({ nome: "", telefono: "", servizio: SERVICES[0], messaggio: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/requests`, form);
      toast.success("Richiesta inviata! Ti ricontatterò entro 24 ore.");
      setForm({ nome: "", telefono: "", servizio: SERVICES[0], messaggio: "" });
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivimi su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contatti" data-testid="contact-section" className="bg-pine text-bone py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-terracotta mb-4">Contatti</p>
            <h2 className="font-serif tracking-tight text-3xl sm:text-4xl lg:text-5xl">
              Iniziamo il tuo percorso di recupero.
            </h2>
            <p className="mt-6 text-bone/70 leading-relaxed max-w-md">
              Compila il modulo o contattami direttamente: ti rispondo entro 24 ore per fissare la
              prima valutazione a casa tua.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                data-testid="contact-phone-button"
                href="tel:+393522500222"
                className="flex items-center gap-2 rounded-full border border-bone/30 text-bone font-semibold px-6 py-3.5 hover:border-terracotta hover:text-terracotta transition-colors duration-300"
              >
                <Phone size={17} strokeWidth={1.5} /> +39 352 250 0222
              </a>
              <a
                data-testid="contact-whatsapp-button"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-terracotta text-bone font-semibold px-6 py-3.5 hover:bg-bone hover:text-pine transition-colors duration-300"
              >
                <MessageCircle size={17} strokeWidth={1.5} /> Scrivimi su WhatsApp
              </a>
              <a
                data-testid="contact-instagram-button"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-bone text-pine font-semibold px-6 py-3.5 hover:bg-sand transition-colors duration-300"
              >
                <Instagram size={17} strokeWidth={1.5} /> Seguimi su Instagram
              </a>
            </div>
            <ul className="mt-12 space-y-5">
              {INFO.map((i) => (
                <li key={i.label} className="flex items-center gap-4">
                  <i.icon size={18} strokeWidth={1.5} className="text-terracotta shrink-0" />
                  <span className="text-xs uppercase tracking-[0.16em] text-bone/50 w-24">{i.label}</span>
                  {i.href ? (
                    <a href={i.href} className="text-sm text-bone hover:text-sand transition-colors duration-300">{i.value}</a>
                  ) : (
                    <span className="text-sm text-bone">{i.value}</span>
                  )}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-bone/[0.06] border border-bone/15 rounded-md p-8 lg:p-10 backdrop-blur-sm"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-bone/60">Nome e cognome</span>
                  <input
                    data-testid="contact-name-input"
                    required
                    value={form.nome}
                    onChange={set("nome")}
                    placeholder="Mario Rossi"
                    className="mt-2 w-full bg-transparent border-b border-bone/25 focus:border-terracotta outline-none py-2.5 text-bone placeholder:text-bone/30 transition-colors duration-300"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-bone/60">Telefono</span>
                  <input
                    data-testid="contact-phone-input"
                    required
                    type="tel"
                    value={form.telefono}
                    onChange={set("telefono")}
                    placeholder="333 000 0000"
                    className="mt-2 w-full bg-transparent border-b border-bone/25 focus:border-terracotta outline-none py-2.5 text-bone placeholder:text-bone/30 transition-colors duration-300"
                  />
                </label>
              </div>
              <label className="block mt-6">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-bone/60">Servizio richiesto</span>
                <select
                  data-testid="contact-service-select"
                  value={form.servizio}
                  onChange={set("servizio")}
                  className="mt-2 w-full bg-pine border border-bone/25 rounded-md focus:border-terracotta outline-none py-3 px-3 text-bone transition-colors duration-300"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="block mt-6">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-bone/60">Raccontami il tuo problema</span>
                <textarea
                  data-testid="contact-message-input"
                  rows={4}
                  value={form.messaggio}
                  onChange={set("messaggio")}
                  placeholder="Da quanto tempo, dove senti dolore, terapie già fatte..."
                  className="mt-2 w-full bg-transparent border-b border-bone/25 focus:border-terracotta outline-none py-2.5 text-bone placeholder:text-bone/30 resize-none transition-colors duration-300"
                />
              </label>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 w-full flex items-center justify-center gap-2 rounded-full bg-terracotta text-bone font-semibold px-8 py-4 hover:bg-bone hover:text-pine disabled:opacity-60 transition-colors duration-300"
              >
                <Send size={16} strokeWidth={1.5} />
                {sending ? "Invio in corso..." : "Invia la richiesta"}
              </button>
              <p className="mt-4 text-xs text-bone/40 leading-relaxed">
                Inviando il modulo acconsenti al trattamento dei dati per essere ricontattato. Leggi
                la{" "}
                <a href="/privacy" data-testid="contact-privacy-link" className="underline hover:text-bone/70 transition-colors duration-300">
                  Privacy Policy
                </a>
                . Nessuno spam, solo la tua risposta.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
